import { useMemo } from 'react';
import { AxisBottom, AxisLeft, Gridlines, labelStride } from './Axis';
import { ChartFrame } from './ChartFrame';
import { CursorLayer } from './CursorLayer';
import { categoryLabels, longestLabelLength, longestSeriesKeys } from './categories';
import { areaPath, linePath, pointsFor, valueExtentFor } from './geometry';
import {
  type BandScale,
  type LinearScale,
  bandScale,
  linearScale,
  niceDomain,
  tickCountFor,
} from './scale';
import { markerIsStroke, markerPath, seriesColor, seriesDash, seriesMarker } from './series';
import type { ChartMargin, Formatter, PlotBox, Series } from './types';
import { type ChartCursor, useChartCursor } from './useChartCursor';

interface LineChartProps {
  title: string;
  summary?: string | undefined;
  series: readonly Series[];
  /**
   * Fills under the line. Ignored with two or more series — overlapping fills
   * hide whichever is drawn second, and a reader cannot tell which.
   */
  area?: boolean | undefined;
  /**
   * Anchor the axis at zero. Default true.
   *
   * Set false only where zero is genuinely meaningless — a median age, a pH.
   * Not for "the changes are small and I want to see them": that is precisely
   * the case where an amplified axis misleads.
   */
  zeroBased?: boolean | undefined;
  /** A dashed rule, e.g. the committed turnaround. */
  reference?: { value: number; label: string } | undefined;
  format?: Formatter | undefined;
  height?: number | undefined;
  margin?: Partial<ChartMargin> | undefined;
  categoryLabel?: string | undefined;
  onSelect?: ((key: string) => void) | undefined;
}

/**
 * One to six series over a shared categorical axis.
 *
 * Spacing is by category index, not by elapsed time. Every series the analytics
 * endpoints return is bucketed and zero-filled server-side, so the categories
 * are evenly spaced by construction — and positioning by timestamp would
 * silently disagree with the axis labels for any series with a gap in it.
 */
export function LineChart({
  title,
  summary,
  series,
  area = false,
  zeroBased = true,
  reference,
  format = String,
  height = 240,
  margin,
  categoryLabel = 'Period',
  onSelect,
}: LineChartProps) {
  const keys = useMemo(() => longestSeriesKeys(series), [series]);
  const labels = useMemo(() => categoryLabels(series), [series]);

  const x = useMemo(() => bandScale(keys, [0, 1], 0), [keys]);
  const cursor = useChartCursor(x, 0, onSelect);

  return (
    <ChartFrame
      title={title}
      summary={summary}
      series={series}
      height={height}
      margin={margin}
      format={format}
      categoryLabel={categoryLabel}
      overlay={(plot) => (
        <CursorLayer
          cursor={cursor}
          series={series}
          format={format}
          x={bandScale(keys, [0, plot.inner.width], 0)}
          width={plot.inner.width}
        />
      )}
    >
      {(plot) => (
        <LinePlot
          plot={plot}
          layout={{ keys, labels, series, area, zeroBased, reference, format }}
          cursor={cursor}
        />
      )}
    </ChartFrame>
  );
}

/** What the plot draws, settled before the plot's size is known. */
interface LineLayout {
  keys: readonly string[];
  labels: Map<string, string>;
  series: readonly Series[];
  area: boolean;
  zeroBased: boolean;
  reference?: { value: number; label: string } | undefined;
  format: Formatter;
}

function LinePlot({
  plot,
  layout,
  cursor,
}: {
  plot: PlotBox;
  layout: LineLayout;
  cursor: ChartCursor;
}) {
  const { keys, labels, series, reference, format } = layout;
  const { width, height: inner } = plot.inner;
  // `padding: 0` so the first and last points sit on the plot edges — a
  // time series should start where its axis starts.
  const scaleX = bandScale(keys, [0, width], 0);

  const domain = niceDomain(
    ...valueExtentFor(series, layout.zeroBased, reference?.value),
    tickCountFor(inner, 40),
  );
  const y = linearScale(domain, [inner, 0]);
  const ticks = y.ticks(tickCountFor(inner, 40));

  return (
    <>
      <Gridlines y={y} plot={plot} ticks={ticks} />
      <AxisLeft y={y} ticks={ticks} format={format} />
      <AxisBottom
        x={scaleX}
        plot={plot}
        labels={labels}
        every={labelStride(keys.length, width, longestLabelLength(labels))}
      />

      {reference && <ReferenceRule reference={reference} y={y(reference.value)} width={width} />}

      {cursor.key !== undefined && (
        <line
          className="c-chart__cursor-rule"
          aria-hidden="true"
          x1={scaleX.centre(cursor.key)}
          x2={scaleX.centre(cursor.key)}
          y1={0}
          y2={inner}
        />
      )}

      {series.map((one, index) => (
        <SeriesPath
          key={one.id}
          series={one}
          index={index}
          x={scaleX}
          y={y}
          area={layout.area && series.length === 1}
          baseline={y(Math.max(0, domain[0]))}
          activeKey={cursor.key}
        />
      ))}

      {/* One transparent rectangle catches every pointer event, so
          tracking does not depend on hitting a 2px line. */}
      <rect
        x={0}
        y={0}
        width={width}
        height={inner}
        fill="transparent"
        onPointerMove={cursor.onPointerMove}
        onPointerLeave={cursor.onPointerLeave}
      />
    </>
  );
}

/** A dashed horizontal rule with its label at the right-hand end. */
function ReferenceRule({
  reference,
  y,
  width,
}: {
  reference: { label: string };
  y: number;
  width: number;
}) {
  return (
    <g aria-hidden="true">
      <line className="c-chart__reference" x1={0} x2={width} y1={y} y2={y} />
      <text className="c-chart__reference-label" x={width} y={y - 4} textAnchor="end">
        {reference.label}
      </text>
    </g>
  );
}

interface SeriesPathProps {
  series: Series;
  index: number;
  x: BandScale;
  y: LinearScale;
  area: boolean;
  baseline: number;
  activeKey: string | undefined;
}

function SeriesPath({ series, index, x, y, area, baseline, activeKey }: SeriesPathProps) {
  const points = pointsFor(series.points, x, y);
  const colour = seriesColor(index, series.tone);
  const marker = seriesMarker(index);
  const stroked = markerIsStroke(marker);
  const active = points.find((point) => point?.datum.key === activeKey);

  return (
    <g aria-hidden="true">
      {area && <path d={areaPath(points, baseline)} fill={colour} opacity={0.12} />}
      <path
        className="c-chart__line"
        d={linePath(points)}
        stroke={colour}
        strokeDasharray={seriesDash(index)}
      />
      {/* Markers on every point only while they can stay distinct. Past two
          dozen they merge into a thick line, and the dash carries the identity
          instead. */}
      {points.length <= 24 &&
        points.map(
          (point) =>
            point && (
              <path
                key={point.datum.key}
                className="c-chart__marker"
                d={markerPath(marker, 6)}
                transform={`translate(${point.x} ${point.y})`}
                fill={stroked ? 'none' : colour}
                stroke={colour}
              />
            ),
        )}
      {active && (
        <circle className="c-chart__dot" cx={active.x} cy={active.y} r={4} fill={colour} />
      )}
    </g>
  );
}
